import React from "react"
import PropTypes from "prop-types"
import { Button } from "antd"
import { useTranslation } from "react-i18next"
import { Modal } from "ui/Modal"
import CustomDrawer from "ui/Drawer"
import { useDevice } from "hooks/useDevice"
import createdIcon from "assets/icons/blackout-created.svg"
import styles from "./index.module.css"

/**
 * The receipt shown after a blackout is saved over bookings that are already on the calendar
 * (Figma 7476:151792).
 *
 * It exists because the blackout takes effect immediately but the bookings it clashes with do
 * not cancel themselves — that is the guide's call, booking by booking. So the modal states how
 * many need attention and hands over a pre-filtered list rather than closing silently.
 *
 * `onReviewBookings` opens that list in a new tab, per the design's note: the guide is mid-flow
 * on the settings page and should not lose it.
 */
const BlackoutCreatedModal = ({ isOpen, count, isEdit = false, onClose, onReviewBookings }) => {
  const { t } = useTranslation()
  const { isMobile } = useDevice()

  const title = isEdit
    ? t("settings.blackout.updated_title", { defaultValue: "Blackout Updated" })
    : t("settings.blackout.created_title", { defaultValue: "Blackout Created" })

  const message =
    count === 1
      ? t("settings.blackout.created_bookings_single", { defaultValue: "1 upcoming booking requires manual action." })
      : t("settings.blackout.created_bookings", { defaultValue: "{{count}} upcoming bookings require manual action.", count })

  const body = (
    <div className={styles.body}>
      <img src={createdIcon} alt="" className={styles.icon} width="80" height="80" />
      <div className={styles.text}>
        {!isMobile && <h2 className={styles.title}>{title}</h2>}
        <p className={styles.message}>{message}</p>
      </div>
      <Button type="primary" shape="round" size="large" onClick={onReviewBookings} className={styles.action}>
        {t("settings.blackout.review_bookings", { defaultValue: "Review Bookings" })}
      </Button>
    </div>
  )

  if (isMobile) {
    return (
      <CustomDrawer open={isOpen} setOpen={onClose} title={title}>
        {body}
      </CustomDrawer>
    )
  }

  return (
    <Modal open={isOpen} onCancel={onClose} width={660} centered footer={null}>
      {body}
    </Modal>
  )
}

BlackoutCreatedModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  /** Bookings that fall inside the blackout and need the guide to cancel or reschedule them. */
  count: PropTypes.number.isRequired,
  isEdit: PropTypes.bool,
  onClose: PropTypes.func.isRequired,
  onReviewBookings: PropTypes.func.isRequired
}

export default BlackoutCreatedModal
